import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { trackAuthEvent, trackError } from '../services/analytics';
import ProgressDialog from './ProgressDialog';

/**
 * AuthCallback component handles authentication redirects
 * This component processes email confirmations, password resets, and other auth flows
 */
const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [processingAuth, setProcessingAuth] = useState(true);
  const [error, setError] = useState<string | null>(null);

  console.log(`[AuthCallback] Component mounted. pathname: ${location.pathname}, search: ${location.search}`);

  useEffect(() => {
    console.log(`[AuthCallback] useEffect triggered. pathname: ${location.pathname}, search: ${location.search}`);

    async function handleAuthRedirect() {
      try {
        setProcessingAuth(true);
        setError(null);

        const params = new URLSearchParams(location.search);
        const userId = params.get('userId');
        const secret = params.get('secret');
        const type = params.get('type');
        const serviceKey = params.get('serviceKey') || 'default';
        const errorParam = params.get('error');
        const error_description = params.get('error_description');

        console.log('AuthCallback - Processing:', {
          userId: userId ? 'present' : 'missing',
          secret: secret ? 'present' : 'missing',
          type,
          serviceKey,
          error: errorParam,
          error_description,
          fullURL: location.search,
          pathname: location.pathname
        });

        // Handle errors
        if (errorParam) {
          console.error('Auth callback error:', errorParam, error_description);
          trackError(`Auth callback error: ${errorParam} - ${error_description}`, false);
          navigate(`/${serviceKey}?error=${encodeURIComponent(error_description || errorParam)}`);
          return;
        }

        // Handle password recovery verification
        if (userId && secret && type === 'recovery') {
          console.log(`[AuthCallback] Handling password recovery for user: ${userId}`);
          // Redirect to password reset page with userId and secret
          navigate(`/${serviceKey}?mode=reset_password&userId=${userId}&secret=${secret}`);
          trackAuthEvent('Password Recovery Callback', 'email', userId);
          return;
        }

        // Handle email verification (if implemented in GraphQL API)
        if (userId && secret && type === 'verification') {
          console.log(`[AuthCallback] Email verification for user: ${userId}`);
          // Note: Email verification would need to be implemented in the GraphQL API
          trackAuthEvent('Email Verification Callback', 'email', userId);
          navigate(`/${serviceKey}?verified=true`);
          return;
        }

        // No valid token provided, redirect to service page
        console.log('[AuthCallback] No valid auth token provided, redirecting to service page:', `/${serviceKey}`);
        navigate(`/${serviceKey}`);
      } catch (err) {
        console.error('Auth callback exception:', err);
        trackError(`Auth callback exception: ${err instanceof Error ? err.message : String(err)}`, false);
        const serviceKey = new URLSearchParams(location.search).get('serviceKey') || 'default';
        navigate(`/${serviceKey}?error=${encodeURIComponent('Authentication failed. Please try again.')}`);
      } finally {
        setProcessingAuth(false);
      }
    }

    handleAuthRedirect();
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <ProgressDialog isOpen={processingAuth} message="Processing authentication..." subMessage="Please wait while we verify your request and redirect you." />

      {error && !processingAuth && (
        <div className="bg-destructive/10 text-destructive p-6 rounded-lg max-w-md">
          <h2 className="text-xl font-bold mb-2">Authentication Error</h2>
          <p className="mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
          >
            Return to Home
          </button>
        </div>
      )}
    </div>
  );
};

export default AuthCallback;
